import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Brain, Target, TrendingUp, Users, ShieldCheck, Award, Zap, Lock,
  ArrowRight, CheckCircle, BarChart3, FlaskConical, Smartphone, Building2,
  Mail, Crown, Calendar
} from 'lucide-react';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell
} from 'recharts';

const ACCESS_CODE = 'GW2026';
const STORAGE_KEY = 'investor_access_v1';

// Sample BrainMap visualization (representative — not a real user)
const BRAINMAP_DEMO = [
  { subject: 'Memory', score: 87 },
  { subject: 'Recall', score: 84 },
  { subject: 'Speed', score: 92 },
  { subject: 'Pattern', score: 78 },
  { subject: 'Focus', score: 86 }
];

const FUND_USE = [
  { label: 'Premium Content', amount: 200000, pct: 40, color: '#09090B' },
  { label: 'User Acquisition', amount: 150000, pct: 30, color: '#09090B' },
  { label: 'Compliance & Legal', amount: 50000, pct: 10, color: '#09090B' },
  { label: 'Infrastructure & Domain', amount: 50000, pct: 10, color: '#52525b' },
  { label: 'Working Capital', amount: 50000, pct: 10, color: '#71717a' }
];

function GateScreen({ onUnlock }) {
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');

  const submit = (e) => {
    e.preventDefault();
    if (code.trim().toUpperCase() === ACCESS_CODE) {
      sessionStorage.setItem(STORAGE_KEY, '1');
      onUnlock();
    } else {
      setErr('Invalid access code');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#09090B] text-white" data-testid="investor-gate">
      <div className="container-swiss max-w-md">
        <div className="border border-zinc-800 p-10">
          <Lock className="w-10 h-10 text-white mb-5" />
          <p className="mono-data text-zinc-400 mb-1">Confidential</p>
          <h1 className="text-3xl font-black tracking-tighter mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Investor Access
          </h1>
          <p className="text-sm text-zinc-400 mb-8">
            This deck is shared by invitation only. Enter the access code provided by FOKYES.
          </p>
          <form onSubmit={submit} className="space-y-4">
            <input
              type="password"
              value={code}
              onChange={e => { setCode(e.target.value); setErr(''); }}
              placeholder="Access code"
              className="w-full bg-transparent border border-zinc-700 px-4 py-3 text-sm text-white focus:border-white focus:outline-none mono-data tracking-widest"
              data-testid="investor-code-input"
              autoFocus
            />
            {err && <p className="text-xs text-zinc-300" data-testid="investor-code-error">{err}</p>}
            <button type="submit"
              className="w-full bg-white text-[#09090B] py-3 text-sm font-bold tracking-widest uppercase hover:bg-zinc-200 transition-colors"
              data-testid="investor-unlock-btn">
              Unlock Deck
            </button>
          </form>
          <p className="text-xs text-zinc-600 mt-6 text-center">
            <Link to="/" className="hover:text-zinc-400">← Back to FOKYES</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default function InvestorPitch() {
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem(STORAGE_KEY) === '1');

  if (!unlocked) return <GateScreen onUnlock={() => setUnlocked(true)} />;

  return (
    <div className="min-h-screen bg-white" data-testid="investor-pitch-page">
      {/* HERO */}
      <section className="border-b border-[#09090B] bg-[#09090B] text-white py-20 md:py-28 relative overflow-hidden">
        <div className="container-swiss relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7">
              <p className="mono-data text-zinc-400 mb-4">Seed Round · Q2 2026</p>
              <h1 className="text-5xl md:text-7xl font-black tracking-tighter leading-[0.95] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="investor-hero-headline">
                Measure the brain<br/>like Whoop measures<br/><span className="text-white">the body.</span>
              </h1>
              <p className="text-lg md:text-xl text-zinc-300 max-w-xl leading-relaxed mb-10">
                FOKYES turns cognitive training into a measurable sport for Gen Z.
                One BrainMap Score. Five dimensions. Real growth, scientifically tracked.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#ask" className="bg-white text-[#09090B] px-8 py-4 text-sm font-bold tracking-widest uppercase hover:bg-zinc-200 transition-colors flex items-center justify-center gap-2" data-testid="investor-cta-ask">
                  ₹5,00,000 Seed Ask <ArrowRight className="w-4 h-4" />
                </a>
                <Link to="/" target="_blank" className="border border-zinc-600 text-white px-8 py-4 text-sm font-bold tracking-widest uppercase hover:bg-white hover:text-black transition-colors flex items-center justify-center gap-2" data-testid="investor-cta-demo">
                  Try Live Demo
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 flex items-center justify-center">
              <div className="relative" data-testid="investor-hero-radar">
                <ResponsiveContainer width={380} height={380}>
                  <RadarChart data={BRAINMAP_DEMO}>
                    <PolarGrid stroke="#27272a" />
                    <PolarAngleAxis dataKey="subject" tick={{ fill: '#a1a1aa', fontSize: 12, fontFamily: 'JetBrains Mono' }} />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} stroke="#27272a" />
                    <Radar dataKey="score" stroke="#09090B" fill="#09090B" fillOpacity={0.25} strokeWidth={2.5} />
                  </RadarChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="text-center">
                    <div className="text-5xl font-black tracking-tighter text-[#09090B]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>763</div>
                    <div className="mono-data text-[10px] text-zinc-500 mt-1">/ 900 BRAINMAP</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="border-b border-[#09090B] py-20" data-testid="problem-section">
        <div className="container-swiss">
          <p className="mono-data text-[#09090B] mb-3">The Problem</p>
          <h2 className="text-4xl md:text-5xl font-black tracking-tighter mb-12 max-w-3xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
            India's Gen Z is losing 4 hours of attention every day. Nobody is measuring it.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#09090B]">
            <div className="p-8 md:border-r border-b md:border-b-0 border-[#09090B]">
              <Smartphone className="w-7 h-7 text-[#09090B] mb-4" />
              <div className="text-5xl font-black tracking-tighter mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>7.4h</div>
              <p className="mono-data text-xs text-zinc-500 mb-3">DAILY SCREEN TIME · INDIAN TEENS</p>
              <p className="text-sm text-zinc-600">Average time on phones, up 2.3x since 2019. Most of it: short-form video, gaming, social.</p>
            </div>
            <div className="p-8 md:border-r border-b md:border-b-0 border-[#09090B]">
              <BarChart3 className="w-7 h-7 text-[#09090B] mb-4" />
              <div className="text-5xl font-black tracking-tighter mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>−47%</div>
              <p className="mono-data text-xs text-zinc-500 mb-3">DROP IN SUSTAINED ATTENTION</p>
              <p className="text-sm text-zinc-600">Compared to teens 10 years ago. Linked to academic decline, anxiety, and identity issues.</p>
            </div>
            <div className="p-8">
              <Target className="w-7 h-7 text-[#09090B] mb-4" />
              <div className="text-5xl font-black tracking-tighter mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>0</div>
              <p className="mono-data text-xs text-zinc-500 mb-3">SCIENTIFIC METRIC PARENTS USE</p>
              <p className="text-sm text-zinc-600">There's no FitBit for the brain. Parents see the symptoms but cannot measure the cause.</p>
            </div>
          </div>
          <p className="text-sm text-zinc-500 italic mt-6 max-w-2xl">
            "I know my child is changing. I just don't know if anything I do actually works." — every parent we've spoken to.
          </p>
        </div>
      </section>

      {/* SOLUTION */}
      <section className="border-b border-[#09090B] py-20 bg-zinc-50" data-testid="solution-section">
        <div className="container-swiss">
          <p className="mono-data text-[#09090B] mb-3">The Solution</p>
          <h2 className="text-4xl md:text-5xl font-black tracking-tighter mb-4 max-w-4xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
            One hour. Five dimensions. A score that means something.
          </h2>
          <p className="text-lg text-zinc-600 max-w-2xl mb-12">
            Like a Whoop strap for cognition. Daily 1-hour sessions train memory, focus, recall, pattern recognition, and consistency — and produce a single, defensible BrainMap Score (0–900) parents can track.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 border border-[#09090B]">
            <div className="p-8 lg:border-r border-b lg:border-b-0 border-[#09090B]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-[#09090B] flex items-center justify-center text-white font-black text-sm">1</div>
                <h3 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Daily 1-hour sessions</h3>
              </div>
              <p className="text-sm text-zinc-600 leading-relaxed">
                JSON-driven session player with 8 block types. AI adapts difficulty in real time using GPT-4. 36 progressive sessions across 5 free programs.
              </p>
            </div>
            <div className="p-8 lg:border-r border-b lg:border-b-0 border-[#09090B] bg-[#09090B] text-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-[#09090B] flex items-center justify-center text-white font-black text-sm">2</div>
                <h3 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>BrainMap Score 0–900</h3>
              </div>
              <p className="text-sm text-zinc-300 leading-relaxed">
                5 cognitive dimensions, 180 points each. Updated after every session. Levels: Developing → Building → Proficient → Advanced. The number parents trust.
              </p>
            </div>
            <div className="p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-[#09090B] flex items-center justify-center text-white font-black text-sm">3</div>
                <h3 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Parent Dashboard</h3>
              </div>
              <p className="text-sm text-zinc-600 leading-relaxed">
                Weekly digest, peer benchmarks, screen-time vs. brain-score correlation. Parents see exactly how their child is growing — with DPDP-compliant verifiable consent.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT'S BUILT */}
      <section className="border-b border-[#09090B] py-20" data-testid="proof-section">
        <div className="container-swiss">
          <p className="mono-data text-[#09090B] mb-3">Proof of Execution</p>
          <h2 className="text-4xl md:text-5xl font-black tracking-tighter mb-4 max-w-3xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Already shipped. Already working.
          </h2>
          <p className="text-zinc-600 mb-12 max-w-2xl">13 development phases complete. 77/77 automated tests passing. Real Razorpay integration live. DPDP/COPPA/GDPR compliance shipped. <strong>This isn't a deck — it's a product.</strong></p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-[#09090B] mb-12">
            {[
              { num: '36', label: 'Sessions live' },
              { num: '25', label: 'Programs' },
              { num: '50+', label: 'API endpoints' },
              { num: '77', label: 'Automated tests' }
            ].map((s, i) => (
              <div key={i} className={`p-6 border-r border-[#09090B] last:border-r-0 ${i < 2 ? 'border-b md:border-b-0' : ''}`}>
                <div className="text-4xl md:text-5xl font-black tracking-tighter text-[#09090B]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{s.num}</div>
                <p className="mono-data text-[10px] text-zinc-500 mt-2">{s.label.toUpperCase()}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { icon: Brain, title: 'AI Adaptive Engine', text: 'Real-time difficulty adjustment via GPT-4. Cognitive profiles, weak-area detection, personalized feedback.' },
              { icon: Users, title: 'Parent Dashboard', text: 'Weekly digest, peer benchmarks, screen-time correlation. DPDP/COPPA-compliant parental consent flow.' },
              { icon: Crown, title: 'Razorpay Subscriptions', text: 'Live ₹299/month auto-renewing Pro plan + one-time program purchases. Webhook verified.' },
              { icon: Building2, title: 'B2B College System', text: 'CSV student upload, organization dashboards, performance analytics. Ready for NGO + CSR partnerships.' },
              { icon: ShieldCheck, title: 'DPDP/COPPA/GDPR Pack', text: 'Full compliance: age gate, parental consent, data export, account deletion, audit trail. Pre-launch ready.' },
              { icon: FlaskConical, title: 'Creator Marketplace', text: '70/30 revenue split. Independent creators can publish programs and earn — supply-side moat.' }
            ].map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="border border-[#09090B] p-6 hover:bg-zinc-50 transition-colors">
                  <Icon className="w-6 h-6 text-[#09090B] mb-3" />
                  <h4 className="font-bold text-base mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{f.title}</h4>
                  <p className="text-sm text-zinc-600">{f.text}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-10 p-6 border border-[#09090B] bg-zinc-100 flex items-center gap-4">
            <CheckCircle className="w-6 h-6 text-[#09090B] flex-shrink-0" />
            <p className="text-sm text-zinc-700">
              <strong>Tech stack:</strong> React · FastAPI · MongoDB · Razorpay · Resend · OpenAI GPT-4. Production-grade. Deployable today.
            </p>
          </div>
        </div>
      </section>

      {/* MARKET */}
      <section className="border-b border-[#09090B] py-20 bg-zinc-50" data-testid="market-section">
        <div className="container-swiss">
          <p className="mono-data text-[#09090B] mb-3">The Market</p>
          <h2 className="text-4xl md:text-5xl font-black tracking-tighter mb-12 max-w-3xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
            253 million Indian Gen Z. Parents desperate for measurable answers.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#09090B]">
            <div className="p-8 md:border-r border-b md:border-b-0 border-[#09090B]">
              <p className="mono-data text-xs text-zinc-500 mb-2">TAM (India)</p>
              <div className="text-4xl font-black tracking-tighter mb-3" style={{ fontFamily: 'JetBrains Mono, monospace' }}>₹38,000 Cr</div>
              <p className="text-sm text-zinc-600">EdTech + cognitive wellness market by 2028. Growing 18% YoY post-BYJU's reset.</p>
            </div>
            <div className="p-8 md:border-r border-b md:border-b-0 border-[#09090B]">
              <p className="mono-data text-xs text-zinc-500 mb-2">SAM (Elite Parents)</p>
              <div className="text-4xl font-black tracking-tighter mb-3" style={{ fontFamily: 'JetBrains Mono, monospace' }}>₹4,200 Cr</div>
              <p className="text-sm text-zinc-600">Households with income &gt;₹15L/yr who already spend on tutors, coaches, screen-time tools.</p>
            </div>
            <div className="p-8">
              <p className="mono-data text-xs text-zinc-500 mb-2">SOM (Year 1)</p>
              <div className="text-4xl font-black tracking-tighter mb-3" style={{ fontFamily: 'JetBrains Mono, monospace' }}>₹2 Cr</div>
              <p className="text-sm text-zinc-600">10,000 paying subscribers at ₹299/mo + 5 NGO/CSR partnerships in metros.</p>
            </div>
          </div>
          <p className="text-xs text-zinc-500 mt-4">Sources: IAMAI, BCG India, Boston Consulting Group EdTech Report 2025. Numbers are estimates for fundraising context.</p>
        </div>
      </section>

      {/* THE ASK */}
      <section className="border-b border-[#09090B] py-20 bg-[#09090B] text-white" id="ask" data-testid="ask-section">
        <div className="container-swiss">
          <p className="mono-data text-zinc-400 mb-3">The Ask</p>
          <h2 className="text-5xl md:text-6xl font-black tracking-tighter mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            ₹5,00,000
          </h2>
          <p className="text-lg text-zinc-300 max-w-2xl mb-12">
            12-month runway to validate product-market fit, ship premium content, acquire first 1,000 paying parents, and establish 3 NGO partnerships.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h3 className="mono-data text-xs text-zinc-400 mb-4">USE OF FUNDS</h3>
              <div className="space-y-3" data-testid="fund-breakdown">
                {FUND_USE.map((f, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="w-2 h-12" style={{ background: f.color }} />
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-semibold">{f.label}</span>
                        <span className="mono-data text-xs">₹{(f.amount / 1000).toFixed(0)}K · {f.pct}%</span>
                      </div>
                      <div className="h-1 bg-zinc-800 w-full">
                        <div className="h-full" style={{ width: `${f.pct}%`, background: f.color }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="mono-data text-xs text-zinc-400 mb-4">12-MONTH MILESTONES</h3>
              <ul className="space-y-3 text-sm">
                {[
                  ['Q1', 'Live Razorpay + Resend domain · 100 parent waitlist'],
                  ['Q2', '20 premium programs shipped · 1st NGO pilot live'],
                  ['Q3', '1,000 paying subscribers · Mobile app launch'],
                  ['Q4', 'Series A readiness · 3 NGO partnerships · ₹30L ARR']
                ].map(([q, m], i) => (
                  <li key={i} className="flex items-start gap-3 py-2 border-b border-zinc-800">
                    <span className="mono-data text-xs text-zinc-400 w-8 flex-shrink-0 pt-0.5">{q}</span>
                    <span className="text-zinc-300">{m}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WHY NOW */}
      <section className="border-b border-[#09090B] py-20" data-testid="why-now-section">
        <div className="container-swiss">
          <p className="mono-data text-[#09090B] mb-3">Why Now</p>
          <h2 className="text-4xl md:text-5xl font-black tracking-tighter mb-12 max-w-3xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Three forces converging in 2026.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: ShieldCheck, title: 'DPDP Act enforcement', text: 'India\'s data law forces compliance from day 1. We already shipped it. Most competitors are 18 months behind.' },
              { icon: TrendingUp, title: 'Post-BYJU\'s vacuum', text: 'EdTech trust is broken. Outcome-based, transparent, scientific platforms win. We are that platform.' },
              { icon: Users, title: 'CSR mandate momentum', text: 'Companies must spend 2% on CSR. Cognitive development for underprivileged Gen Z is now a board-level priority.' }
            ].map((w, i) => {
              const Icon = w.icon;
              return (
                <div key={i} className="border border-[#09090B] p-6">
                  <Icon className="w-7 h-7 text-[#09090B] mb-4" />
                  <h4 className="font-bold text-lg mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{w.title}</h4>
                  <p className="text-sm text-zinc-600 leading-relaxed">{w.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* DEMO CTA */}
      <section className="border-b border-[#09090B] py-20 bg-[#09090B] text-white" data-testid="demo-cta-section">
        <div className="container-swiss text-center max-w-3xl mx-auto">
          <Zap className="w-10 h-10 mx-auto mb-6" />
          <h2 className="text-4xl md:text-5xl font-black tracking-tighter mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Don't trust the deck. Try the product.
          </h2>
          <p className="text-lg text-white/90 mb-10 max-w-xl mx-auto">
            Click below, sign in with Google, complete a 1-hour session. See your own BrainMap Score in 60 minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/" target="_blank" className="bg-white text-[#09090B] px-10 py-4 text-sm font-black tracking-widest uppercase hover:bg-black hover:text-white transition-colors flex items-center justify-center gap-2" data-testid="investor-try-now">
              Try the live product <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/programs" target="_blank" className="border-2 border-white text-white px-10 py-4 text-sm font-black tracking-widest uppercase hover:bg-white hover:text-[#09090B] transition-colors">
              Browse programs
            </Link>
          </div>
          <p className="mono-data text-xs text-white/70 mt-8">
            Demo tip: Use Razorpay test card <strong>4111 1111 1111 1111</strong> · any future expiry · any CVV to demo a Pro purchase.
          </p>
        </div>
      </section>

      {/* CONTACT */}
      <section className="py-20 bg-[#09090B] text-white" data-testid="contact-section">
        <div className="container-swiss text-center max-w-2xl mx-auto">
          <p className="mono-data text-zinc-400 mb-3">Get in Touch</p>
          <h2 className="text-3xl md:text-4xl font-black tracking-tighter mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            FOKYES
          </h2>
          <p className="text-zinc-400 mb-10 max-w-lg mx-auto">
            For terms, equity discussion, due diligence, or to schedule a live walkthrough — reach out through your introduction.
          </p>
          <div className="flex items-center justify-center gap-2 text-sm text-zinc-500">
            <Mail className="w-4 h-4" />
            <span className="mono-data">Reply to your introduction email</span>
          </div>
          <p className="text-xs text-zinc-600 mt-12">
            © {new Date().getFullYear()} FOKYES · This document is confidential and shared by invitation only · BrainMap Score is an educational tool, not a medical or diagnostic assessment.
          </p>
        </div>
      </section>
    </div>
  );
}
